/**
 * User Management V2 endpoints.
 *
 * Paths/methods aligned to the canonical monolith
 * (`backend/server.monolith.js`) route declarations.  Codex's refactor
 * backend (`backend/src/routes/users.routes.js` and
 * `exchange-user-services.routes.js`) preserves the same path/method
 * contract per the parity scaffolding, so these wrappers work against
 * both the monolith and the migrated handlers without changes.
 */

import { apiGet, apiPost, apiPut, apiDelete, buildQuery } from '../client.js';

/* ============================================================
 * Search & directory
 * ============================================================ */

export const fetchBusinessUnits        = ()                  => apiGet('/api/ad-business-units');
// Business short code for a business (+ optional site) — used to auto-suggest
// group names (e.g. SG-<code>-<share>). Mirrors the beta /api/ad/business-code.
export const fetchBusinessCode         = (business, site)    => apiGet(`/api/ad/business-code${buildQuery({ business, site })}`);
export const fetchSitesForBusiness     = (business)          => apiGet(`/api/ad/sites/${encodeURIComponent(business)}`);
// site is optional — omit it to list everyone in the business.
export const fetchUsersForSite         = (business, site)    => apiGet(`/api/ad/site-users${buildQuery({ business, site: site || undefined })}`);
// Suggested-groups feature (create-user wizard): users in a specific OU with
// their live group memberships — `[{ Name, SamAccountName, Groups: [dn, ...] }]`.
export const fetchSiteUsersWithGroups  = (ou)                => apiGet(`/api/ad/site-users-with-groups${buildQuery({ ou })}`);
// business/site are optional — when set the search is scoped to that business
// (and site) server-side, before the result cap.
export const searchUsers               = (query, scope)      => apiGet(`/api/ad/search-users${buildQuery({ query, business: scope?.business || undefined, site: scope?.site || undefined })}`);
// Quick-filter browse (no query): type = disabled | locked | taccounts | inactive
export const fetchFilteredUsers        = (type)              => apiGet(`/api/ad/filter-users?type=${encodeURIComponent(type)}`);
// fetchAllUsers was removed 2026-09-01: its /api/ad/users/all route never
// existed in apps/api (guaranteed 404) and nothing called this wrapper — the
// Permissions tab's fetchAllUsers lives in adminSettings.js (/api/users/all,
// which IS backed). Do not reintroduce without building the backend route first.
export const fetchAcceptedDomains      = ()                  => apiGet('/api/ad/accepted-domains');
export const fetchUpnSuffixes          = ()                  => apiGet('/api/ad/upn-suffixes');
// User search index refresh status — `{ ok, data: { ..., indexStats: { count,
// storage, updatedAt }, lastError } }`. See apps/api/src/routes/user-management.ts.
export const fetchUserSearchRefreshStatus = ()               => apiGet('/api/user-management/search/refresh-status');
export const startUserSearchRefresh       = ()               => apiPost('/api/user-management/search/refresh');

/* ============================================================
 * User details + AD mutations
 * ============================================================ */

export const fetchUserDetails          = (dn)                => apiGet(`/api/ad/user-details${buildQuery({ dn })}`);
export const fetchUserDetailsFast      = (dn)                => apiGet(`/api/ad/user-details-fast${buildQuery({ dn })}`);
export const fetchUserAzureDetails     = (upn)               => apiGet(`/api/ad/user-azure-details${buildQuery({ upn })}`);
export const fetchUserByDn             = (dn)                => apiGet(`/api/ad/user-by-dn${buildQuery({ dn })}`);
// `refresh: true` bypasses the 2-minute server cache. Pass it straight after a
// membership write — otherwise the stale-while-revalidate read serves the old
// list and the group the operator just removed stays on screen (see
// apps/api/src/modules/user-management/user-groups-cache.ts).
export const fetchUserGroups           = (upn, { refresh } = {}) => apiGet(`/api/ad/user-groups${buildQuery({ upn, refresh: refresh ? 'true' : undefined })}`);
export const userPhotoUrl              = (upn)               => `/api/ad/user-photo${buildQuery({ upn })}`;

export const updateUser                = (body)              => apiPost('/api/ad/update-user', { body });
export const changeEmail               = (body)              => apiPost('/api/ad/change-email', { body });
export const changePassword            = (body)              => apiPost('/api/ad/change-password', { body });
export const unlockAccount             = (body)              => apiPost('/api/ad/unlock-account', { body });
export const moveUser                  = (body)              => apiPost('/api/ad/move-user', { body });

/* ============================================================
 * Create user
 * ============================================================ */

export const createUser                = (body)              => apiPost('/api/ad/create-user', { body });
export const createGenericUser         = (body)              => apiPost('/api/ad/create-generic-user', { body });
export const checkUserExists           = (username, upn)     => apiGet(`/api/ad/check-user-exists${buildQuery({ username, upn })}`);
export const fetchCreateUserStatus     = (upn)               => apiGet(`/api/ad/create-user-status${buildQuery({ upn })}`);

/* ============================================================
 * Leaver
 *
 * The monolith does NOT expose a separate GET /api/ad/leaver-details.
 * The wizard reads the existing user details + user-devices and POSTs
 * to /api/ad/process-leaver to run the workflow.
 * ============================================================ */

export const processLeaver             = (body)              => apiPost('/api/ad/process-leaver', { body });
export const fetchUserDevices          = (upn)               => apiGet(`/api/ad/user-devices${buildQuery({ upn })}`);
export const wipeDevices               = (body)              => apiPost('/api/ad/wipe-devices', { body });
export const syncDevice                = (body)              => apiPost('/api/ad/sync-device', { body });

/* Assign Device — search every Intune managed device (min 2 chars), then set
 * one's primary user. Search returns { query, devices: [...] } (the client
 * unwraps the { ok, data } envelope). */
export const searchIntuneDevices       = (query)             => apiGet(`/api/user-management/intune/devices/search${buildQuery({ query })}`);
export const assignDeviceToUser        = (body)              => apiPost('/api/ad/assign-device', { body });
/* ============================================================
 * Proxy addresses
 * Monolith uses POST for all three (add/delete/update).
 * ============================================================ */

export const addProxyAddress           = (body)              => apiPost('/api/ad/proxy-address/add', { body });
export const removeProxyAddress        = (body)              => apiPost('/api/ad/proxy-address/delete', { body });
export const updateProxyAddress        = (body)              => apiPost('/api/ad/proxy-address/update', { body });
export const setPrimaryProxyAddress    = (body)              => apiPost('/api/ad/proxy-address/set-primary', { body });
export const checkProxyAddress         = (body)              => apiPost('/api/ad/proxy-address/check', { body });

/* ============================================================
 * Group membership
 * ============================================================ */

export const addUserToGroup            = (body)              => apiPost('/api/ad/add-user-to-group', { body });
export const removeUserFromGroup       = (body)              => apiPost('/api/ad/remove-user-from-group', { body });
export const fetchAllGroups            = ()                  => apiGet('/api/groups/all');
export const searchGroupsForUser       = (query)             => apiGet(`/api/ad/search-groups${buildQuery({ query })}`);
export const searchGroupsLive          = (query, limit = 25) => apiGet(`/api/user-management/groups/search${buildQuery({ query, limit })}`);
// Mail-enabled groups only (distribution / M365 / mail-enabled security), each
// carrying a primary SMTP `email` — used as forwarding/recipient targets.
// Graph-backed (see service.searchMailEnabledGroups). Returns { ok, data: { query, groups } }.
export const searchMailEnabledGroups   = (query, limit = 25) => apiGet(`/api/user-management/groups/mail-search${buildQuery({ query, limit })}`);
export const fetchUserDrives           = (upn)               => apiGet(`/api/drives/user/${encodeURIComponent(upn)}`);

/* ============================================================
 * Mail delegation (per-user routes — monolith canonical)
 *   GET    /api/users/:upn/mail-delegations
 *   POST   /api/users/:upn/mail-delegations
 *   DELETE /api/users/:upn/mail-delegations/:delegateUPN
 * ============================================================ */

export const fetchMailDelegations      = (upn)               => apiGet(`/api/users/${encodeURIComponent(upn)}/mail-delegations`);
export const addMailDelegation         = (upn, body)         => apiPost(`/api/users/${encodeURIComponent(upn)}/mail-delegations`, { body });
export const removeMailDelegation      = (upn, delegateUpn)  => apiDelete(`/api/users/${encodeURIComponent(upn)}/mail-delegations/${encodeURIComponent(delegateUpn)}`);
// Setter, not a toggle — Exchange never reports the current auto-mapping value.
export const setMailDelegationAutoMapping = (upn, delegateUpn, autoMapping) =>
  apiPost(`/api/users/${encodeURIComponent(upn)}/mail-delegations/${encodeURIComponent(delegateUpn)}/auto-mapping`, { body: { autoMapping } });

/* ============================================================
 * Mail forwarding (per-user routes — monolith canonical)
 *   GET    /api/users/:upn/mail-forwarding
 *   POST   /api/users/:upn/mail-forwarding
 *   DELETE /api/users/:upn/mail-forwarding[/:forwardingAddress]
 * ============================================================ */

export const fetchMailForwarding       = (upn)               => apiGet(`/api/users/${encodeURIComponent(upn)}/mail-forwarding`);
export const setMailForwarding         = (upn, body)         => apiPost(`/api/users/${encodeURIComponent(upn)}/mail-forwarding`, { body });
export const removeMailForwarding      = (upn, forwardingAddress) =>
  apiDelete(`/api/users/${encodeURIComponent(upn)}/mail-forwarding${forwardingAddress ? `/${encodeURIComponent(forwardingAddress)}` : ''}`);

/* ============================================================
 * Calendar permissions (Exchange-scoped, upn route param on GET)
 *   GET    /api/exchange/calendar-permissions/:upn
 *   POST   /api/exchange/calendar-permissions   (body has mailbox + delegate)
 *   DELETE /api/exchange/calendar-permissions   (body has mailbox + delegate)
 * ============================================================ */

// List all calendar folders (primary + secondary) for a mailbox.
export const fetchCalendarFolders      = (upn)               => apiGet(`/api/exchange/calendar-folders/${encodeURIComponent(upn)}`);
// `folder` (optional) targets a specific secondary calendar by its FolderPath.
export const fetchCalendarPermissions  = (upn, folder)       => apiGet(`/api/exchange/calendar-permissions/${encodeURIComponent(upn)}${folder ? buildQuery({ folder }) : ''}`);
// Create a new (secondary) calendar folder. Body: { mailbox, name }.
export const createCalendarFolder      = (body)              => apiPost('/api/exchange/calendar-folders', { body });
export const addCalendarPermission     = (body)              => apiPost('/api/exchange/calendar-permissions', { body });
export const removeCalendarPermission  = (body)              => apiDelete('/api/exchange/calendar-permissions', { body });

/* ============================================================
 * Mailbox rules (Exchange-scoped, upn route param)
 *   GET    /api/exchange/mailbox-rules/:upn
 *   DELETE /api/exchange/mailbox-rules/:upn  (body identifies the rule)
 * The monolith does not expose POST to create rules from the UM tab.
 * ============================================================ */

export const fetchMailboxRules         = (upn)               => apiGet(`/api/exchange/mailbox-rules/${encodeURIComponent(upn)}`);
export const removeMailboxRule         = (upn, body)         => apiDelete(`/api/exchange/mailbox-rules/${encodeURIComponent(upn)}`, { body });

/* ============================================================
 * Archive — user mailbox flavour
 * ============================================================ */

export const enableArchive                  = (body)         => apiPost('/api/ad/enable-archive', { body });
export const disableArchive                 = (body)         => apiPost('/api/ad/disable-archive', { body });
export const enableAutoExpandingArchive     = (body)         => apiPost('/api/ad/enable-auto-expanding-archive', { body });
export const setRetentionPolicy             = (body)         => apiPost('/api/ad/set-archive-policy', { body });
export const runUserManagedFolderAssistant  = (body)         => apiPost('/api/ad/run-managed-folder-assistant', { body });
export const enableLitigationHold           = (body)         => apiPost('/api/ad/enable-litigation-hold', { body });
export const disableLitigationHold          = (body)         => apiPost('/api/ad/disable-litigation-hold', { body });
export const fetchArchivePolicies           = ()             => apiGet('/api/ad/archive-policies');
// Backend requires `?email=` (matches monolith Get-Mailbox identity), not `?upn=`.
export const fetchMailboxSize               = (upn)          => apiGet(`/api/ad/mailbox-size${buildQuery({ email: upn })}`);

/* ============================================================
 * Automatic replies (Out of Office)
 *   GET  /api/ad/mailbox-autoreply?email=
 *   POST /api/ad/mailbox-autoreply  body { email, autoReplyState, internalMessage,
 *                                          externalMessage, externalAudience, startTime, endTime }
 * ============================================================ */
export const fetchAutoReply                 = (upn)          => apiGet(`/api/ad/mailbox-autoreply${buildQuery({ email: upn })}`);
export const setAutoReply                   = (body)         => apiPost('/api/ad/mailbox-autoreply', { body });

/* ============================================================
 * OneDrive admin (per-user routes — monolith canonical)
 *   GET    /api/users/:upn/onedrive-admins
 *   POST   /api/users/:upn/onedrive-admins         body { adminUPN }
 *   DELETE /api/users/:upn/onedrive-admins/:adminUPN
 *   GET    /api/users/:upn/onedrive-shared
 * ============================================================ */

export const fetchOnedriveAdmins   = (upn)                   => apiGet(`/api/users/${encodeURIComponent(upn)}/onedrive-admins`);
export const addOnedriveAdmin      = (upn, adminUpn)         => apiPost(`/api/users/${encodeURIComponent(upn)}/onedrive-admins`, { body: { adminUPN: adminUpn } });
export const removeOnedriveAdmin   = (upn, adminUpn)         => apiDelete(`/api/users/${encodeURIComponent(upn)}/onedrive-admins/${encodeURIComponent(adminUpn)}`);
export const fetchOnedriveShared   = (upn)                   => apiGet(`/api/users/${encodeURIComponent(upn)}/onedrive-shared`);
export const fetchOnedriveInfo     = (upn)                   => apiGet(`/api/ad/onedrive-info${buildQuery({ upn })}`);
// Inline permission edit/remove for OneDrive shared items route through the
// generic user-action dispatcher (apps/api routes/user-management.ts).
export const updateOnedriveSharedPermission = (upn, itemId, permissionId, role) =>
  apiPost(`/api/user-management/users/${encodeURIComponent(upn)}/actions`, { body: { action: 'updateOneDriveSharedPermission', payload: { itemId, permissionId, role } } });
export const removeOnedriveSharedPermission = (upn, itemId, permissionId) =>
  apiPost(`/api/user-management/users/${encodeURIComponent(upn)}/actions`, { body: { action: 'removeOneDriveSharedPermission', payload: { itemId, permissionId } } });

/* ============================================================
 * MFA
 * ============================================================ */

export const fetchMfaMethods            = (upn)              => apiGet(`/api/ad/mfa-methods${buildQuery({ userPrincipalName: upn })}`);
export const removeMfaMethod            = (body)             => apiPost('/api/ad/remove-mfa-method', { body });
export const revokeMfaSessions          = (body)             => apiPost('/api/ad/revoke-mfa-sessions', { body });
export const requireMfaReregister       = (body)             => apiPost('/api/ad/require-mfa-reregister', { body });
export const setDefaultMfaMethod        = (body)             => apiPost('/api/ad/mfa-set-default', { body });
// moveToInactiveOu was removed 2026-09-01: its /api/ad/move-to-inactive-ou route
// never existed in apps/api (guaranteed 404) and nothing called the wrapper —
// parking accounts in the Inactive OU is done server-side by the stale-accounts
// automation. Do not reintroduce without building the backend route first.
export const restoreInactiveUser        = (userDN)           => apiPost('/api/ad/restore-inactive-user', { body: { userDN } });

/* ============================================================
 * Sign-in history
 * ============================================================ */

export const fetchAzureSignin           = (upn)              => apiGet(`/api/users/${encodeURIComponent(upn)}/azure-signin`);
export const fetchAzureSigninAll        = (upn)              => apiGet(`/api/users/${encodeURIComponent(upn)}/azure-signin/all`);

/* ============================================================
 * BitLocker / LAPS diagnostics
 * ============================================================ */

// Both take the Intune managed device ID (?deviceId=), not the device name.
export const fetchBitlockerKeys         = (deviceId)         => apiGet(`/api/ad/bitlocker${buildQuery({ deviceId })}`);
export const fetchLapsPassword          = (deviceId)         => apiGet(`/api/ad/laps${buildQuery({ deviceId })}`);

/* ============================================================
 * Email
 *   POST /api/send-email  (ad-hoc "Send Email" to a user)
 * ============================================================ */

export const sendEmail                  = (body)             => apiPost('/api/send-email', { body });

/* ============================================================
 * Licence / host pool / drives (used by the create wizard)
 * ============================================================ */

export const fetchLicenseGroups         = ()                 => apiGet('/api/license-groups');
export const fetchHostPools             = ()                 => apiGet('/api/host-pools');
export const fetchAllDrives             = ()                 => apiGet('/api/drives');
export const fetchCreateCatalog         = ()                 => apiGet('/api/user-management/create/catalog');

/* ============================================================
 * Deprecated wrappers that previous components imported.  Kept as
 * thin shims so existing callers don't break while we update each
 * modal to the new (upn-first) signature.
 *
 * Each of these should be removed once its caller is migrated.
 * ============================================================ */

/** @deprecated Use updateUser({ dn, UserPrincipalName, SamAccountName, ... }) */
export const fetchLeaverDetails         = (_dn)              => Promise.resolve({
  liveSourceAvailable: false,
  note: 'There is no GET /api/ad/leaver-details in the monolith — the leaver wizard derives details from the existing user record.'
});
